/**
 * screens/Overview — Finance · "Cash position"
 * ──────────────────────────────────────────────────────────────────────────
 * Who holds company money right now.
 *
 * Four KPI cards, two charts, the count-results bar and a worst-first
 * "Needs attention" table. Everything on the screen shares ONE as-of moment,
 * because GET /v1/overview computes it all in a single transaction — five
 * separate requests would produce a page whose KPI and whose chart disagree by
 * a few seconds, which in a cash subledger is a support ticket.
 *
 * INVARIANT 4 IS VISIBLE HERE. There is no single "total cash in custody"
 * figure anywhere on this screen, because there is no honest way to produce one
 * without an exchange rate and a date stamp. The largest currency is the 22px
 * value and the others sit on the sub line as "+ INR 12,400.00". The charts are
 * base-currency-only and say so, using the label the API supplies.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useCallback, useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import {
  ChartCard,
  DataTable,
  EmptyState,
  Grid,
  KpiCard,
  Loading,
  Money,
  PageContainer,
  PageHeader,
  StackedBar,
  BarRow,
  LegendRow,
  Card,
  CardHeader,
  type Column,
} from '../../components';
import { kpiAccent, neutral, series as seriesColors } from '../../theme/colors';
import { card as cardSpace, gridMin } from '../../theme/spacing';
import { mono, sans, size as fontSize } from '../../theme/typography';
import { ellipsisStyle } from '../../theme/webStyle';
import { useAsyncData } from '../../lib/useAsyncData';
import { format, formatRest, splitTotals } from '../../lib/money';
import { oldestLabel } from '../../lib/dates';
import { countLabel, dotted, sentenceCase } from '../../lib/format';
import { reconStatus, toneOf } from '../../lib/status';
import { fetchOverview } from '../../services/overview/overview.service';
import { fetchQueueSummary } from '../../services/approvals/approvals.service';
import { fetchAssignments } from '../../services/cash/cash.service';
import type { Assignment } from '../../schemas/cash';
import type { AttentionRow, Overview } from '../../schemas/overview';
import { SCOPES } from '../../schemas/cash';
import type { QueueSummary } from '../../schemas/approvals';
import { useShell } from '../../context/ShellContext';
import { OVERVIEW_COPY, OVERVIEW_FLOW, OVERVIEW_STEPS } from './emptyState';

/** The scope colours, in the design's own order. */
const SCOPE_COLOR: Record<string, string> = {
  open: seriesColors.open,
  purpose: seriesColors.purpose,
  collection: seriesColors.collection,
};

/**
 * What each attention row asks of the reader. The API tells us the KIND of
 * problem; this says what closing it looks like, which is what the design's
 * third column is for.
 */
const ATTENTION_ACTION: Record<string, string> = {
  variance: 'route or waive',
  approval: 'awaiting a decision',
  separation: 'settle before the last day',
  stale: 'needs a count',
  dead_letter: 'retry the delivery',
};

export function OverviewScreen() {
  const { dataVersion, openDrawer, openModal, setView } = useShell();

  /**
   * Both calls in one hook so the screen has ONE loading state and one failure
   * path. The queue summary is here only for the pending-approvals card's
   * "oldest …" sub line, which the overview payload does not carry.
   */
  const load = useCallback(
    async (): Promise<{
      overview: Overview;
      queue: QueueSummary | null;
      assignments: Assignment[];
    }> => {
      const [overview, queue, assignments] = await Promise.all([
        fetchOverview(),
        // A Finance user holds the approval scope, but if that ever changes the
        // card should lose its sub line, not the screen its data.
        fetchQueueSummary().catch(() => null),
        /*
         * The design's "Needs attention" row shows the holder, its scope and
         * its instrument. The overview payload carries only `label`/`detail`
         * and an `assignment_id`, so the register is fetched alongside and
         * joined on that id — which is also what makes the row's balance the
         * assignment's real balance rather than the variance amount.
         */
        fetchAssignments().catch(() => []),
      ]);
      return { overview, queue, assignments };
    },
    [],
  );

  const { data, loading } = useAsyncData(load, [], {
    version: dataVersion,
    errorTitle: 'Could not load the cash position',
  });

  const overview = data?.overview ?? null;

  /**
   * The design's "Needs attention" is ONE ROW PER CUSTODIAN, worst first. The
   * API returns one row per ISSUE, so an assignment with two pending approvals
   * and a stale count arrives three times — and once the rows are rendered with
   * the design's columns (holder, scope, instrument, balance) those three are
   * indistinguishable.
   *
   * Keeping the highest-severity issue per assignment reproduces the design and
   * loses nothing: the KPI cards above already count the issues by kind, and
   * the row opens the ledger where all of them are visible.
   */
  const attention = useMemo(() => {
    if (!overview) return [];
    const worstPerAssignment = new Map<string, AttentionRow>();
    const unassigned: AttentionRow[] = [];
    for (const row of overview.needs_attention) {
      if (!row.assignment_id) {
        unassigned.push(row);
        continue;
      }
      const existing = worstPerAssignment.get(row.assignment_id);
      if (!existing || row.severity > existing.severity) {
        worstPerAssignment.set(row.assignment_id, row);
      }
    }
    return [...unassigned, ...worstPerAssignment.values()].sort(
      (a, b) => b.severity - a.severity,
    );
  }, [overview]);

  /** assignment_id → assignment, for the needs-attention join. */
  const assignmentsById = useMemo(() => {
    const map = new Map<string, Assignment>();
    for (const a of data?.assignments ?? []) map.set(a.id, a);
    return map;
  }, [data]);

  /* ── Derived figures ───────────────────────────────────────────────────
   * Memoised against the payload, not against render. The design handoff
   * measured the prototype recomputing every derived figure on each form
   * keystroke at ~33ms; keeping the derived layer keyed to the data it depends
   * on is the fix, and it is why nothing here reads component state.
   */
  const kpis = useMemo(() => {
    if (!overview) return null;
    const custody = splitTotals(overview.kpis.cash_in_custody);
    const unreconciled = splitTotals(overview.kpis.unreconciled);
    const variance = splitTotals(overview.kpis.open_variance);
    const awaitingRouting = overview.needs_attention.filter((r) => r.kind === 'variance').length;

    return {
      custody,
      unreconciled,
      variance,
      awaitingRouting,
      staleDays: overview.kpis.stale_days,
      pending: overview.kpis.pending_approvals,
    };
  }, [overview]);

  const scopeMix = useMemo(() => {
    if (!overview) return [];
    /*
     * Scope is a fixed three-value enum, and the design's bar and legend run in
     * that order — open, purpose, collection. Rendering them in whatever order
     * the response happened to use would make the chart's colours move between
     * loads.
     */
    const order = (scope: string) => {
      const i = SCOPES.indexOf(scope as (typeof SCOPES)[number]);
      return i === -1 ? SCOPES.length : i;
    };
    return [...overview.charts.by_scope]
      .sort((a, b) => order(a.scope) - order(b.scope))
      .map((row) => ({
      key: row.scope,
      value: Number(row.total_minor),
      color: SCOPE_COLOR[row.scope] ?? neutral.grey400,
      label: row.scope,
      count: row.count,
      total: row.total_minor,
    }));
  }, [overview]);

  const topHolders = useMemo(() => {
    if (!overview) return { rows: [], max: '0' };
    const rows = overview.charts.top_holders;
    // Bars are scaled to the LARGEST holder, not to the total — five holders of
    // similar size scaled to the total are five indistinguishable stubs.
    const max = rows.reduce((biggest, row) => {
      return Number(row.total_minor) > Number(biggest) ? row.total_minor : biggest;
    }, '0');
    return { rows, max };
  }, [overview]);

  const countMix = useMemo(() => {
    if (!overview) return { segments: [], total: 0 };
    const segments = overview.charts.count_results.map((row) => {
      const tone = reconStatus(row.status);
      return { key: row.status, value: row.count, color: toneOf(tone.tone).dot, label: tone.label };
    });
    return { segments, total: segments.reduce((sum, s) => sum + s.value, 0) };
  }, [overview]);

  /* ── Needs attention table ─────────────────────────────────────────────
   * The design's grid: `1.6fr 0.9fr 0.9fr 1fr 24px` with a 12px gap. The whole
   * row is clickable and opens the assignment's ledger; rows that are not about
   * one assignment (a dead-letter batch) are not clickable, because there is
   * nothing to open.
   */
  const columns = useMemo<Column<AttentionRow>[]>(
    () => [
      {
        key: 'holder',
        header: '',
        track: { fr: 1.6 },
        stackedLabel: 'Holder',
        render: (row) => {
          const assignment = row.assignment_id ? assignmentsById.get(row.assignment_id) : undefined;
          return (
            <View style={styles.cellStack}>
              <Text numberOfLines={1} style={styles.primary}>
                {assignment?.holder ?? row.label}
              </Text>
              <Text numberOfLines={1} style={styles.meta}>
                {assignment
                  ? dotted(assignment.holder_type, assignment.holder_ref)
                  : row.detail}
              </Text>
            </View>
          );
        },
      },
      {
        key: 'scope',
        header: '',
        track: { fr: 0.9 },
        stackedLabel: 'Scope',
        render: (row) => {
          const assignment = row.assignment_id ? assignmentsById.get(row.assignment_id) : undefined;
          // A row that is not about one assignment (a dead-letter batch) has no
          // scope. Showing its kind there keeps the column meaningful rather
          // than inventing one.
          return (
            <Text numberOfLines={1} style={styles.scope}>
              {assignment ? assignment.scope : sentenceCase(row.kind)}
            </Text>
          );
        },
      },
      {
        key: 'instrument',
        header: '',
        track: { fr: 0.9 },
        stackedLabel: 'Instrument',
        render: (row) => {
          const assignment = row.assignment_id ? assignmentsById.get(row.assignment_id) : undefined;
          // Only an instrument gets the capitalised treatment — running it over
          // the fallback sentence turned "retry the delivery" into
          // "Retry The Delivery".
          return assignment ? (
            <Text numberOfLines={1} style={styles.instrument}>
              {sentenceCase(assignment.instrument)}
            </Text>
          ) : (
            <Text numberOfLines={1} style={styles.instrumentPlain}>
              {ATTENTION_ACTION[row.kind] ?? '—'}
            </Text>
          );
        },
      },
      {
        key: 'amount',
        header: '',
        track: { fr: 1 },
        align: 'right',
        stackedLabel: 'Balance',
        render: (row) => {
          const assignment = row.assignment_id ? assignmentsById.get(row.assignment_id) : undefined;
          if (assignment) {
            return <Money value={assignment.balance_minor} cur={assignment.cur} align="right" />;
          }
          return row.amount_minor && row.cur ? (
            <Money value={row.amount_minor} cur={row.cur} align="right" />
          ) : (
            <Text style={styles.dash}>—</Text>
          );
        },
      },
    ],
    [assignmentsById],
  );

  if (loading && !overview) {
    return (
      <PageContainer>
        <Loading />
      </PageContainer>
    );
  }

  /* ── Empty: the educational panel, not "No data" ───────────────────────── */
  const isEmpty = !!overview && overview.kpis.cash_in_custody.length === 0;

  return (
    <PageContainer testID="screen-overview">
      <PageHeader title="Cash position" subtitle="Who holds company money right now." />

      {isEmpty || !overview || !kpis ? (
        <EmptyState
          eyebrow={OVERVIEW_COPY.eyebrow}
          title={OVERVIEW_COPY.title}
          blurb={OVERVIEW_COPY.blurb}
          nodes={OVERVIEW_FLOW}
          flowNote={OVERVIEW_COPY.flowNote}
          steps={OVERVIEW_STEPS}
          footNote={OVERVIEW_COPY.footNote}
          ctaLabel="Assign cash"
          onCta={() => openModal({ kind: 'assign' })}
        />
      ) : (
        <>
          {/* ── Four KPI cards ─────────────────────────────────────────── */}
          <View style={styles.block}>
            <Grid min={gridMin.kpi} gap={cardSpace.kpiGap}>
              <KpiCard
                label="Cash in custody"
                accent={kpiAccent.cashInCustody}
                value={
                  kpis.custody.largest
                    ? format(kpis.custody.largest.total_minor, kpis.custody.largest.cur)
                    : '—'
                }
                sub={
                  kpis.custody.rest.length > 0
                    ? formatRest(kpis.custody.rest)
                    : kpis.custody.largest
                      ? countLabel(kpis.custody.largest.count, 'assignment')
                      : undefined
                }
                onPress={() => setView('register')}
              />
              <KpiCard
                label="Unreconciled assignments"
                accent={kpiAccent.unreconciled}
                tone="pending"
                value={
                  kpis.unreconciled.largest
                    ? format(kpis.unreconciled.largest.total_minor, kpis.unreconciled.largest.cur)
                    : '—'
                }
                sub={`older than ${kpis.staleDays} days`}
                onPress={() => setView('recon')}
              />
              <KpiCard
                label="Open variance"
                accent={kpiAccent.openVariance}
                tone="danger"
                value={
                  kpis.variance.largest
                    ? format(kpis.variance.largest.total_minor, kpis.variance.largest.cur)
                    : '—'
                }
                sub={`${kpis.awaitingRouting} awaiting routing`}
                onPress={() => setView('recon')}
              />
              <KpiCard
                label="Pending approvals"
                accent={kpiAccent.pendingApprovals}
                tone="warning"
                value={String(kpis.pending)}
                sub={oldestLabel(data?.queue?.oldest_at) ?? undefined}
                onPress={() => setView('approvals')}
              />
            </Grid>
          </View>

          {/* ── Two charts ─────────────────────────────────────────────── */}
          <View style={styles.block}>
            <Grid min={gridMin.chart} gap={cardSpace.gridGap}>
              <ChartCard title="Where the cash sits" note={overview.charts.labelled}>
                <View style={styles.chartBar}>
                  <StackedBar segments={scopeMix} />
                </View>
                <View style={styles.legend}>
                  {scopeMix.map((segment) => (
                    <LegendRow
                      key={segment.key}
                      color={segment.color}
                      label={segment.label}
                      meta={countLabel(segment.count, 'assignment')}
                      value={segment.total}
                      cur={overview.charts.base_currency ?? ''}
                    />
                  ))}
                </View>
              </ChartCard>

              <ChartCard title="Who holds the most" note={`top ${topHolders.rows.length}`}>
                <View style={styles.holders}>
                  {topHolders.rows.map((holder) => (
                    <BarRow
                      key={`${holder.holder}-${holder.holder_type}`}
                      label={holder.holder}
                      // The design shows the holder's name alone here — the
                      // type belongs in the register, not in a chart row.
                      value={holder.total_minor}
                      cur={overview.charts.base_currency ?? ''}
                      max={topHolders.max}
                    />
                  ))}
                </View>
              </ChartCard>
            </Grid>
          </View>

          {/* ── Count results this period ──────────────────────────────── */}
          <View style={styles.block}>
            <ChartCard
              title="Count results this period"
              note={`${countMix.total} counted`}
            >
              <View style={styles.chartBarTight}>
                <StackedBar segments={countMix.segments} />
              </View>
              <View style={styles.countLegend}>
                {countMix.segments.map((segment) => (
                  <View key={segment.key} style={styles.countLegendItem}>
                    <View style={[styles.countDot, { backgroundColor: segment.color }]} />
                    <Text style={styles.countLabel}>{segment.label}</Text>
                    <Text style={styles.countValue}>{segment.value}</Text>
                  </View>
                ))}
              </View>
            </ChartCard>
          </View>

          {/* ── Needs attention ───────────────────────────────────────── */}
          <Card>
            <CardHeader
              title="Needs attention"
              filled
              right={<Text style={styles.headerNote}>Worst first</Text>}
            />
            <DataTable
              columns={columns}
              rows={attention}
              keyExtractor={(row) => `${row.kind}:${row.ref_id}`}
              chevron
              // The design gives this table no column headers — the card header
              // ("Needs attention · Worst first") is its only heading.
              hideHeader
              onRowPress={(row) => {
                // A row with no assignment is not about one holder's cash — the
                // sync log is where a dead-letter batch is dealt with.
                if (row.assignment_id) openDrawer(row.assignment_id);
                else if (row.kind === 'dead_letter') setView('router');
                else if (row.kind === 'approval') setView('approvals');
                else if (row.kind === 'separation') setView('settle');
              }}
            />
          </Card>
        </>
      )}
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  // 22px between the design's major blocks on this screen.
  block: { marginBottom: 22 },
  chartBar: { marginBottom: 15 },
  chartBarTight: { marginBottom: 14 },
  legend: { gap: 10 },
  holders: { gap: 12 },
  countLegend: { flexDirection: 'row', flexWrap: 'wrap', gap: 18 },
  countLegendItem: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  countDot: { width: 6, height: 6, borderRadius: 999 },
  countLabel: { ...sans('400'), fontSize: fontSize.button, color: neutral.ink, textTransform: 'capitalize' },
  countValue: { ...mono('400'), fontSize: fontSize.button, color: neutral.ink },
  headerNote: { ...sans('400'), fontSize: fontSize.meta, color: neutral.grey600 },
  cellStack: { minWidth: 0, gap: 1 },
  primary: { ...sans('500'), fontSize: fontSize.body, color: neutral.ink, ...ellipsisStyle },
  meta: { ...mono('400'), fontSize: fontSize.tableHeader, color: neutral.grey400, ...ellipsisStyle },
  // 12px, capitalised, ink-2 — the design's Scope column.
  scope: {
    ...sans('400'),
    fontSize: fontSize.label,
    color: neutral.ink2,
    textTransform: 'capitalize',
    ...ellipsisStyle,
  },
  // 12px grey, capitalised — the design's Instrument column.
  instrument: {
    ...sans('400'),
    fontSize: fontSize.label,
    color: neutral.grey600,
    textTransform: 'capitalize',
    ...ellipsisStyle,
  },
  /** Same, for a sentence that is already correctly cased. */
  instrumentPlain: {
    ...sans('400'),
    fontSize: fontSize.label,
    color: neutral.grey600,
    ...ellipsisStyle,
  },
  dash: { ...mono('400'), fontSize: fontSize.body, color: neutral.grey400 },
});

export default OverviewScreen;
